/**
 * 작성자 : 홍선영
 * 날짜 : 2024.12.05
 * 기능 : (사내)업무관리-근태관리-근태신청 페이지 신청 목록
 */

import { t } from 'i18next';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import SelectBox from '../../../components/SelectBox';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import useDateRange from '../../../hooks/useDateRange';
import { AWAITING_MY_APPROVAL_CODE, withAwaitingMyApprovalOption } from '../../../utils/renderApprovalStatus';
import { useApprovalAlertAt, useConsumeApprovalAlert } from '../../../hooks/useApprovalAlertEntry';
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { COMCD_A_STATUS, COMCD_ATTENDANCE_GUBUN, LIST_COUNT } from '../../../_constants';
import ShortcutButton from '../../../components/button/ShortcutButton';
import { BtnGhost } from '../../../components/Button';
import { IApplicationFormType, IAttendance, IComCdList } from 'customTypes';
import useSetListCount from '../../../utils/useSetListCount';
import { setComCdListState } from '../../../utils/setComCdListState';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import { useRecoilValue } from 'recoil';
import { IUser, userState } from '../../../atoms';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import styled from 'styled-components';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import IssueGuide from '../../../components/IssueGuide';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { PulseLoader } from 'react-spinners';
import 'dayjs/locale/ko'; // 한국어 로케일 추가
import { isImageValid } from '../../../utils/isImageValid';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import TuiGrid from '../../../components/Table/TuiGrid';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import i18n from '../../../translation/i18n';
import CustomLabelsRenderer from '../../../components/CustomLabelsRenderer';
import CustomAttachmentRenderer from '../../../components/CustomAttachmentRenderer';
import { useOutletContext } from 'react-router-dom';
import Portal from '../../../components/Portal';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import { isMobile } from '../../../utils/checkMobile';

interface IList {
  formType: IApplicationFormType;
  approvalType: 'request' | 'approval';
  tableState: IAttendance[] | null;
  setTableState: Dispatch<SetStateAction<IAttendance[] | null>>;
  initTableState: IAttendance[] | null;
  setInitTableState: Dispatch<SetStateAction<IAttendance[] | null>>;
  setRowState: Dispatch<SetStateAction<IAttendance>>;
  isNewAdd: boolean;
  setIsNewAdd: Dispatch<SetStateAction<boolean>>;
  addBtnAction: () => void;
  formatInTuiForm: any;
}

const INIT_ROW = {
  wDate: '',
  eDate: '',
  cCd: '',
  caCd: '',
  caStatus: '0',
  caName: '',
  caDepartment: '',
  emerNum: '',
  address: '',
  caGubun: '',
  sDate: '',
  fDate: '',
  caPdate: '',
  caPtime: '',
  caReason: '',
  caReplacement: '',
  delYn: '',
  writer: '',
  editor: '',
  attendanceD1: [],
  attendanceD2: [],
  currentUserId: '',
  signImg: '',
  uAnnualnum: '0',
  uRannualnum: '0',
};
const S3_URL = process.env.REACT_APP_AWS_ROOT_URL;
const DIRECTORY_NAME = process.env.REACT_APP_AWS_DIRECTORY_NAME;
const Root = styled(ContentsContainerRoot)`
  /* 결재 탭 첫 줄: 처리 상태 글자가 길어져도 조회 기간이 다음 줄로 내려가지 않게, 상신자 입력칸이 줄어든다 */
  .inputsWrapper.approval-search-row {
    flex-wrap: nowrap;
    .name-search {
      min-width: 0;
      flex-shrink: 1;
      input {
        min-width: 3.5rem;
        flex-shrink: 1;
      }
    }
  }
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }

  .iconBtnGroup {
    margin-right: 7px;
  }

  .filterbar {
    left: 10%;
  }

  .table > .tbody > .tr > div {
    color: var(--text-secondary);
    height: 3.375rem;

    .text-tertiary {
      color: var(--text-disabled);
    }

    .ellipsis {
      overflow: hidden;
      text-overflow: ellipsis;
      width: 7rem;
      text-align: center;
    }
  }

  .badge {
    font-weight: 500;
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    /* margin: 0 0.5rem; */
    text-align: center;
    line-height: 1;
    width: fit-content;
  }

  .blue {
    color: var(--primary);
    background-color: var(--tonal-blue);
  }

  .red {
    color: var(--filled-red);
    background-color: var(--tonal-red);
  }

  .violet {
    color: var(--filled-violet);
    background-color: var(--tonal-violet);
  }

  .orange {
    color: var(--filled-orange);
    background-color: var(--tonal-orange);
  }

  .green {
    color: var(--filled-green);
    background-color: var(--tonal-green);
  }

  .amber {
    color: var(--filled-amber);
    background-color: var(--tonal-amber);
  }

  .slate {
    color: var(--filled-slate);
    background-color: var(--tonal);
  }
`;

const List = ({ formType, approvalType, tableState, setTableState, initTableState, setInitTableState, setRowState, isNewAdd, setIsNewAdd, addBtnAction, formatInTuiForm }: IList) => {
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue<IUser>(userState);
  const [rowKey, setRowKey] = useState('');
  const { dateRange, setDateRange, userInputDateRange, setUserInputDateRange, initiateDate } = useDateRange('2months');
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const approvalAlertAt = useApprovalAlertAt();
  const hasAwaitingOption = approvalType === 'approval' && formType === 'attendance';
  /** 처리 상태. 알림으로 들어왔으면 처음부터 '결재 대기'로 시작한다.
      SelectBox는 처음 붙을 때 자기 선택값을 부모 상태에 다시 써 넣어서, 붙은 뒤에 바꾸면 '전체'로 덮인다 */
  const [caStatus, setCaStatus] = useState(() =>
    hasAwaitingOption && approvalAlertAt
      ? { type: COMCD_A_STATUS, [COMCD_A_STATUS]: AWAITING_MY_APPROVAL_CODE, cdName: t('결재 대기') }
      : { type: COMCD_A_STATUS, [COMCD_A_STATUS]: 'A', cdName: t('전체') }
  );
  const [caGubun, setCaGubun] = useState({ type: COMCD_ATTENDANCE_GUBUN, [COMCD_ATTENDANCE_GUBUN]: 'A', cdName: t('전체') }); // 근태 구분
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const { data: caStatusComCdList, isError: isError1 } = useFetchCommonCodeList(COMCD_A_STATUS, true);
  /** 결재 탭은 처리 상태에 '결재 대기'(내 결재 차례) 옵션을 더한다.
      SelectBox는 options가 바뀔 때마다 선택값을 현재 상태로 되돌리므로 매 렌더 새 배열을 만들지 않게 고정한다 */
  const caStatusOptions = useMemo(() => (hasAwaitingOption ? withAwaitingMyApprovalOption(caStatusComCdList, COMCD_A_STATUS) : caStatusComCdList), [caStatusComCdList, hasAwaitingOption]);
  const { data: caGubunComCdList, isError: isError2 } = useFetchCommonCodeList(COMCD_ATTENDANCE_GUBUN, true);
  const [appNameSearch, setAppNameSearch] = useState('');
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });
  const [columns, setColumns] = useState<any[]>([]);
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  // initTableState 가 목록 조회 응답으로 바뀌었는지. 결재 처리 응답으로 바뀐 경우에는 보던 순서를 유지한다
  const isFetchedListRef = useRef(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const [searchToggle, setSearchToggle] = useState(true);
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅

  const getReqInfo = () => {
    if (formType === 'creport') {
      if (approvalType === 'request') return { path: '/creport' };
      return { path: '/creport/received' };
    }
    if (formType === 'cdraft') {
      if (approvalType === 'request') return { path: '/cdraft' };
      return { path: '/cdraft/history' };
    }
    if (approvalType === 'request') return { path: '/attendance' };
    return { path: '/attendance/history' };
  };

  const { data: approvalApplicationList, isError: isError3 } = GetFetchStatus({
    path: getReqInfo().path,
    hCdOnly: { value: true, key: 'cCd' },
    queryKey: `${searchToggle}_${formType}_${approvalType}`,
  });

  const sortByName1 = (a: any, b: any) => (a?.name1 || '').localeCompare(b?.name1 || '');
  const sortByName2 = (a: any, b: any) => (a?.name2 || '').localeCompare(b?.name2 || '');
  const sortByDate = (_a: any, _b: any, rowA: any, rowB: any) => (rowA?.sDate || '').localeCompare(rowB?.sDate || '');

  const approvalCol = {
    header: t('상신자 / 소속'),
    name: 'appType',
    width: 110,
    align: 'center',
    renderer: { type: CustomLabelsRenderer },
    sortable: true,
    comparator: sortByName1,
  };

  // 첨부 유무 컬럼. 값은 formatInTuiForm 이 넣어주는 fileCount(number)다.
  // 좁은 화면에서 가로 스크롤에 묻히지 않게 고정 컬럼(문서 번호) 바로 뒤에 붙인다
  const attachmentCol = {
    header: t('첨부'),
    name: 'fileCount',
    width: 56,
    align: 'center',
    renderer: { type: CustomAttachmentRenderer },
    sortable: true,
  };

  const fixedSuffixColumns = [
    {
      header: t('문서 번호'),
      name: 'caCd',
      width: 80,
      align: 'center',
      sortable: true,
    },
    attachmentCol,
    {
      header: t('구분 / 상태'),
      name: 'caGubunStatus',
      width: 80,
      align: 'center',
      renderer: { type: CustomLabelsRenderer },
      sortable: true,
      comparator: sortByName1,
    },
    {
      header: t('작성일자'),
      name: 'wDate',
      align: 'center',
      width: 100,
      sortable: true,
    },
    ...(approvalType === 'approval' ? [approvalCol] : []),
    {
      header: t('근태 신청 기간'),
      name: 'period',
      align: 'center',
      minWidth: 120,
      renderer: { type: CustomLabelsRenderer },
      sortable: true,
      comparator: sortByDate,
    },
    ...(approvalType === 'request'
      ? [
          {
            header: t('결재 라인'),
            name: 'appLine',
            align: 'start',
            minWidth: 150,
            sortable: true,
          },
        ]
      : []),
  ];

  const fixedReportColumns = [
    {
      header: t('상신자 / 소속'),
      name: 'appType',
      width: 150,
      align: 'center',
      renderer: { type: CustomLabelsRenderer },
      sortable: true,
      comparator: sortByName1,
    },
    {
      header: t('제목'),
      name: 'title',
      align: 'start',
      minWidth: 120,
      sortable: true,
    },
    {
      header: t('작성일자'),
      name: 'date',
      align: 'center',
      width: 100,
      sortable: true,
    },
  ];

  const fixeDraftColumns = [
    {
      header: t('상태'),
      name: 'lStatus',
      width: 80,
      align: 'center',
      renderer: { type: CustomLabelsRenderer },
      sortable: true,
      comparator: sortByName2,
    },
    {
      header: t('상신자 / 소속'),
      name: 'appType',
      width: 150,
      align: 'center',
      renderer: { type: CustomLabelsRenderer },
      sortable: true,
      comparator: sortByName1,
    },
    {
      header: t('제목'),
      name: 'title',
      align: 'start',
      minWidth: 120,
      sortable: true,
    },
    {
      header: t('작성일자'),
      name: 'date',
      align: 'center',
      width: 100,
      sortable: true,
    },
  ];

  useEffect(() => {
    let col;
    const attendanceColumns = fixedSuffixColumns;
    const reportColumns = fixedReportColumns;
    const draftColumns = fixeDraftColumns;

    if (isMobile() && formType === 'attendance') {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const containerPadding = 0.75 * 2 * rootFontSize;
      const colWidth = Math.floor((document.body.clientWidth - containerPadding) / 4);
      // 첨부 컬럼이 쓸 폭을 먼저 떼어낸 뒤 나머지를 3등분한다.
      // 이 계산이 화면 폭에 맞춰져 있어 3등분 값을 그대로 두고 컬럼만 늘리면 가로로 넘친다
      const attachColWidth = 44;
      const mobileColWidth = Math.floor((document.body.clientWidth - containerPadding - attachColWidth) / 3);
      col = [
        { header: t('구분 / 상태'), name: 'caGubunStatus', align: 'center', width: mobileColWidth, renderer: { type: CustomLabelsRenderer }, sortable: true },
        { header: t('근태 신청 기간'), name: 'period', align: 'center', width: mobileColWidth, renderer: { type: CustomLabelsRenderer }, sortable: true },
        ...(approvalType === 'request'
          ? [{ header: t('결재 라인'), name: 'appLine', align: 'start', width: mobileColWidth, sortable: true }]
          : [{ header: t('상신자 / 소속'), name: 'appType', align: 'center', width: mobileColWidth, renderer: { type: CustomLabelsRenderer }, sortable: true }]),
        // 모바일은 3개 컬럼이 화면 폭에 딱 맞게 계산되어 어디에 두든 다 보인다.
        // 기존 읽는 순서를 흔들지 않도록 맨 뒤에 붙인다
        { ...attachmentCol, width: attachColWidth },
      ];
    } else {
      col = formType === 'attendance' ? attendanceColumns : formType === 'creport' ? reportColumns : draftColumns;
    }

    setColumns(col);
    setCaGubun({ type: COMCD_ATTENDANCE_GUBUN, [COMCD_ATTENDANCE_GUBUN]: 'A', cdName: t('전체') });
  }, [i18n.language, formType, approvalType]);

  useEffect(() => {
    if (approvalApplicationList && caStatusComCdList && caGubunComCdList) {
      isFetchedListRef.current = true;
      setInitTableState(formatInTuiForm(approvalApplicationList));
    }
  }, [approvalApplicationList, formType, caStatusComCdList, caGubunComCdList]);

  useEffect(() => {
    if (isNewAdd) setRowKey('');
  }, [isNewAdd]);

  useEffect(() => {
    if (!initTableState) return;
    /** 결재 탭에서 결재 처리 응답으로 목록이 바뀐 경우 기본 정렬(내 결재 차례 우선)을 다시 적용하지 않는다.
        다시 정렬하면 방금 결재한 건이 뒤로 밀리고, 순번으로 기억하는 선택 행이 다른 문서를 가리킨다 */
    const keepDisplayOrder = approvalType === 'approval' && !isFetchedListRef.current;
    isFetchedListRef.current = false;
    applyFilter(initTableState, keepDisplayOrder);
  }, [initTableState]);

  useEffect(() => {
    setComCdListState(LIST_COUNT, setListCountComCdList, false);
  }, []);

  // f9 keydown 이벤트
  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  const onClickRangeInput = () => {
    if (size.innerSize.W <= 1024) {
      setOpenModal({ status: true, type: 'filter' });
    }
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (filterRef) {
      // 모바일일 경우 검색 필터 모달 창 닫기
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }
    setSearchToggle((prev: boolean) => !prev);
  };

  // 알림으로 들어온 결재 목록은 내 결재 차례인 건만, 오래된 건도 빠지지 않게 조회 기간을 비우고(전체 기간) 다시 조회한다
  useConsumeApprovalAlert(approvalType === 'approval', () => {
    setUserInputDateRange({ start: '', end: '' });
    setCaStatus({ type: COMCD_A_STATUS, [COMCD_A_STATUS]: AWAITING_MY_APPROVAL_CODE, cdName: t('결재 대기') });
    onClickSearch();
  });

  const onClickInitiateSearchOption = () => {
    initiateDate();
    setCaStatus({ type: COMCD_A_STATUS, [COMCD_A_STATUS]: 'A', cdName: t('전체') });
    setCaGubun({ type: COMCD_ATTENDANCE_GUBUN, [COMCD_ATTENDANCE_GUBUN]: 'A', cdName: t('전체') });
    setAppNameSearch('');
  };

  // 기본 정렬: 내가 결재할 차례인 건을 앞으로
  const sortByMyApprovalTurn = (list: IAttendance[]) =>
    [...list].sort((a, b) => {
      const aMatch = a.currentUserId === userInfo.userId ? 0 : 1;
      const bMatch = b.currentUserId === userInfo.userId ? 0 : 1;
      return aMatch - bMatch;
    });

  // 지금 화면에 보이는 순서(그리드 헤더 정렬 포함)대로. 화면에 없던 건은 뒤에 붙인다
  const sortByDisplayOrder = (list: IAttendance[]) => {
    const displayIndex = new Map(filterTableState.map((row, i) => [row.caCd, i]));
    return [...list].sort((a, b) => (displayIndex.get(a.caCd) ?? Infinity) - (displayIndex.get(b.caCd) ?? Infinity));
  };

  const applyFilter = (array: IAttendance[], keepDisplayOrder = false) => {
    // 날짜 문자열을 Date 객체로 변환하는 헬퍼 함수
    const parseDate = (dateStr: string) => {
      const cleanDate = dateStr.split(' ')[0].replaceAll('-', '');
      return new Date(parseInt(cleanDate.substring(0, 4), 10), parseInt(cleanDate.substring(4, 6), 10) - 1, parseInt(cleanDate.substring(6, 8), 10));
    };

    const filteredResult = array.filter((item) => {
      // 처리 상태 필터
      const statusCode = caStatus[COMCD_A_STATUS];
      const statusMatch = statusCode === 'A' || (statusCode === AWAITING_MY_APPROVAL_CODE ? !!(item as any).isAwaitingMyApproval : item.caStatus === statusCode);

      // 근태 구분 필터
      const categoryMatch = caGubun[COMCD_ATTENDANCE_GUBUN] === 'A' || item.caGubun === caGubun[COMCD_ATTENDANCE_GUBUN];

      // 날짜 범위 필터
      let dateMatch = true;
      if (userInputDateRange.start && userInputDateRange.end) {
        const startDate = parseDate(userInputDateRange.start);
        const endDate = parseDate(userInputDateRange.end);
        if (formType === 'attendance') {
          const itemStart = parseDate(item.sDate);
          const itemEnd = parseDate(item.fDate);
          const writtenDate = item.wDate ? parseDate(item.wDate) : null;
          const isWrittenInRange = !!writtenDate && writtenDate >= startDate && writtenDate <= endDate;
          // 근태 신청 기간(시작일~종료일)이 조회 기간과 하루라도 겹치는지
          const isPeriodInRange = itemStart <= endDate && itemEnd >= startDate;
          // 작성일자가 조회 기간 안이거나, 근태 신청 기간이 조회 기간과 겹치면 조회한다
          dateMatch = isWrittenInRange || isPeriodInRange;
        }
        if (formType === 'creport' || formType === 'cdraft') {
          const itemStart = parseDate(item.date);
          const itemEnd = parseDate(item.date);
          dateMatch = itemStart >= startDate && itemEnd <= endDate;
        }
      }

      // 상신자 이름 필터 (결재 탭에서만 적용)
      const trimmed = appNameSearch.trim().toLowerCase();
      const appNameMatch = approvalType !== 'approval' || !trimmed || ((item as any).appType?.name1 ?? '').toLowerCase().includes(trimmed);

      return statusMatch && categoryMatch && dateMatch && appNameMatch;
    });
    const shouldKeepOrder = keepDisplayOrder && filterTableState.length > 0;
    const sorted = shouldKeepOrder ? sortByDisplayOrder(filteredResult) : sortByMyApprovalTurn(filteredResult);
    setTableState(sorted.length > 0 ? sorted.map((el, i) => ({ ...el, rowKey: i })) : []);

    // 선택 행은 순번(rowKey)으로 기억하므로, 보고 있던 문서의 새 순번으로 옮긴다. 목록에서 빠졌으면 선택을 푼다
    if (shouldKeepOrder && rowKey !== '') {
      const selectedCaCd = filterTableState[Number(rowKey)]?.caCd;
      const newIndex = sorted.findIndex((el) => el.caCd === selectedCaCd);
      setRowKey(newIndex >= 0 ? `${newIndex}` : '');
    }

    // filteredList 가 세팅되지 않았을 때
    // filteredAttList === null && setFilteredAttList(filteredResult.length > 0 ? filteredResult.map((el, i) => ({ ...el, rowKey: i })) : []);
  };

  const onClickRow = async (rowData: any, columnName: any, filteredIndex: number) => {
    setIsNewAdd(false);
    if (rowData) {
      setRowKey(`${filteredIndex}`);
      // 상신자 서명정보
      const isValidSignImg = await isImageValid(`${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${rowData.writer}.png`);
      const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
      setRowState({ ...rowData, signImg: isValidSignImg ? `${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${rowData.writer}.png?v=${randomString}` : '' });
    }
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const isComcdLoading = !caStatusComCdList || !caGubunComCdList;
  const isListLoading = tableState === null;
  const isError = isError1 || isError2 || isError3;

  if (isError) return <IssueGuide />;
  if (isComcdLoading) {
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
      </LoadingModalBackground>
    );
  }

  return (
    <>
      <Root loading={isListLoading}>
        <SearchOptions>
          {size.innerSize.W > 1024 ? (
            <>
              <div className={`inputsWrapper ${hasAwaitingOption ? 'approval-search-row' : ''}`}>
                <div className='inputForm-col withLabelComCf'>
                  <label htmlFor='wWorkstatus'>{t('처리 상태')}</label>
                  <SelectBox
                    options={caStatusOptions}
                    defaultOption={t('전체')}
                    state={caStatus}
                    setState={setCaStatus}
                    stateKey={COMCD_A_STATUS}
                    initiateKey={caStatus[COMCD_A_STATUS]}
                    filterbar='filter-1-left'
                  />
                </div>
                {formType === 'attendance' && (
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='wWorkstatus'>{t('근태 구분')}</label>
                    <SelectBox
                      options={caGubunComCdList}
                      defaultOption={t('전체')}
                      state={caGubun}
                      setState={setCaGubun}
                      stateKey={COMCD_ATTENDANCE_GUBUN}
                      initiateKey={caGubun[COMCD_ATTENDANCE_GUBUN]}
                      filterbar='filter-1-left'
                    />
                  </div>
                )}
                {approvalType === 'approval' && (
                  <div className='inputForm-col withLabelComCf name-search'>
                    <label>{t('상신자')}</label>
                    <input type='text' style={{ width: '5.5rem' }} value={appNameSearch} onChange={(e) => setAppNameSearch(e.target.value)} placeholder={t('검색')} />
                  </div>
                )}
                <div className='inputForm-row-fit'>
                  <span className='calendarLabel'>{t(formType === 'attendance' ? '조회 기간' : '보고 기간')}</span>
                  <div className='inputForm-col'>
                    <div className='flex-basic'>
                      <input id='startInput' type='text' value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setDateRange, setUserInputDateRange)} />
                      <span className='inputDash'> ~ </span>
                      <input id='endInput' type='text' value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setDateRange, setUserInputDateRange)} />
                    </div>
                    {visibleRangePicker && (
                      <div className='rangePickerWrapper'>
                        <RangePicker state={dateRange} setState={setDateRange} setVisible={setVisibleRangePicker} />
                      </div>
                    )}
                  </div>
                  <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                    <span className='material-symbols-rounded'>calendar_month</span>
                  </button>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='secondSearchOption'>
                  <div className='flex-basic textBtnGroup'>
                    <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                    <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                    <div className='searchResult'>
                      {t('총')}
                      <span>{tableState?.length ?? 0}</span>
                      {t('개')}
                    </div>
                    <div className='inputForm-row'>
                      <div className='inputForm-col withLabelComCf'>
                        <label htmlFor='useYn'>{t('보기 설정')}</label>
                        <SelectBox
                          options={listCountComCdList}
                          defaultOption={listCount.cdName}
                          state={listCount}
                          setState={setListCount}
                          stateKey={LIST_COUNT}
                          initiateKey={listCount[LIST_COUNT]}
                          setTableLinesAPI={patchUserMenuAPI}
                          optionHeight='height-sm'
                          rsearch
                        />
                      </div>
                    </div>
                  </div>
                  {approvalType === 'request' && (
                    <div className='flex-basic iconBtnGroup'>
                      <BtnGhost onClick={() => addBtnAction()}>
                        <span className='material-symbols-rounded'>add</span>
                        {t('등록')}
                      </BtnGhost>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                  <div className='inputForm-col'>
                    {approvalType === 'approval' ? (
                      <input
                        type='search'
                        value={appNameSearch}
                        onChange={(e) => setAppNameSearch(e.target.value)}
                        placeholder={t('상신자')}
                        style={{ width: '100%', height: '2.5rem', padding: '0 0.75rem', background: 'var(--tonal)', borderRadius: '0.25rem', border: 'none', fontSize: '0.875rem' }}
                      />
                    ) : (
                      <div
                        className='flex-basic'
                        role='presentation'
                        onClick={onClickRangeInput}
                        style={{ background: 'var(--tonal)', borderRadius: '0.25rem', height: '2.5rem', padding: '0 0.5rem', cursor: 'pointer' }}
                      >
                        <input
                          id='startInput'
                          type='text'
                          readOnly
                          value={userInputDateRange.start}
                          onChange={(e) => onChangeRangeInput(e, 'start', setDateRange, setUserInputDateRange)}
                          style={{ textAlign: 'center', background: 'transparent', cursor: 'pointer' }}
                        />
                        <span className='inputDash'> ~ </span>
                        <input
                          id='endInput'
                          type='text'
                          readOnly
                          value={userInputDateRange.end}
                          onChange={(e) => onChangeRangeInput(e, 'end', setDateRange, setUserInputDateRange)}
                          style={{ textAlign: 'center', background: 'transparent', cursor: 'pointer' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <BtnGhost onClick={onClickFilter} className='filter-btn'>
                  <span className='material-symbols-rounded'>filter_alt</span>
                </BtnGhost>
              </div>
              {approvalType === 'request' && (
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={() => addBtnAction()}>
                      <span className='material-symbols-rounded'>add</span>
                      {t('등록')}
                    </BtnGhost>
                  </div>
                </div>
              )}
            </div>
          )}
        </SearchOptions>

        <TuiGridWrapper componentRef={componentRef}>
          {tableState && (
            <TuiGrid
              data={tableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              usePagenation
              onClickRow={onClickRow}
              rowKey={rowKey}
              frozenCount={frozenCount}
              height={tuiHeight}
              setRowKey={setRowKey}
              scrollX
              filterTableState={filterTableState}
              setFilterTableState={setFilterTableState}
              idxKey='not use'
            />
          )}
        </TuiGridWrapper>
      </Root>
      {isListLoading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t(formType === 'attendance' ? '조회 기간' : '보고기간')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setDateRange, setUserInputDateRange)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setDateRange, setUserInputDateRange)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={dateRange} setState={setDateRange} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                {approvalType === 'approval' && (
                  <div className='filter-option-row'>
                    <label>{t('상신자')}</label>
                    <input type='text' value={appNameSearch} onChange={(e) => setAppNameSearch(e.target.value)} placeholder={t('상신자 검색')} />
                  </div>
                )}
                <div className='filter-option-row'>
                  <label htmlFor='wWorkstatus'>{t('처리 상태')}</label>
                  <SelectBox
                    options={caStatusOptions}
                    defaultOption={t('전체')}
                    state={caStatus}
                    setState={setCaStatus}
                    stateKey={COMCD_A_STATUS}
                    initiateKey={caStatus[COMCD_A_STATUS]}
                    filterbar='filter-1-left'
                  />
                </div>
                {formType === 'attendance' && (
                  <div className='filter-option-row'>
                    <label htmlFor='wWorkstatus'>{t('근태 구분')}</label>
                    <SelectBox
                      options={caGubunComCdList}
                      defaultOption={t('전체')}
                      state={caGubun}
                      setState={setCaGubun}
                      stateKey={COMCD_ATTENDANCE_GUBUN}
                      initiateKey={caGubun[COMCD_ATTENDANCE_GUBUN]}
                      filterbar='filter-1-left'
                    />
                  </div>
                )}
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </>
  );
};

export default List;
